import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    // "Choose a size" guided state: the button is enabled but must not add to
    // cart. Skip the added/fly animations; ProductFormComponent#handleSubmit runs
    // the guided-tap (scroll + pulse) and blocks the submit.
    if (this.refs.addToCartButton?.dataset.chooseSize === "true") return;

    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === "true";

    if (animationEnabled && !event.target.closest(".quick-add-modal")) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added");
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added");
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // "Choose a size" guided state: no size is selected yet. Don't add to cart —
    // guide the shopper to the size row (scroll into view + one attention pulse)
    // and bail. Works identically for the inline button and the pinned sticky
    // sheet (same button, same form). Once a size is picked the section re-renders
    // without data-choose-size and the normal submit path resumes.
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.dataset
        .chooseSize === "true"
    ) {
      this.#guideToSizeSelection();
      return;
    }

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        "disabled"
      ) === "true"
    )
      return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component"
    );
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    });

    // Bridal made-to-order lead-time surcharge (snippets/delai-fabrication-picker.liquid).
    // When a PAID lead-time option is selected we must create a SECOND cart line for the
    // surcharge, parented to the garment line — which /cart/add.js only supports via a JSON
    // `items` payload. Everything below is strictly additive: if no picker is present, or it
    // is hidden, or the free (0 EUR) option is selected, `surcharge` stays null and the
    // original FormData request runs byte-identically to before.
    const surcharge = this.#getLeadTimeSurcharge();

    /** @type {RequestInit} */
    let fetchCfg = fetchConfig("javascript", { body: formData });

    // Hoisted so the error handler can reconcile against the garment line it tried to add.
    /** @type {string | null} */
    let variantIdForReconcile = null;

    if (surcharge && typeof formData.get("id") === "string" && formData.get("id")) {
      const variantId = /** @type {string} */ (formData.get("id"));
      variantIdForReconcile = variantId;
      const quantity =
        Number(formData.get("quantity")) ||
        Number(this.dataset.quantityDefault) ||
        1;

      /** @type {Record<string, string>} */
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.*)\]$/);
        if (match && typeof value === "string") properties[match[1]] = value;
      }

      const payload = {
        // ORDER MATTERS FOR DISPLAY. Shopify returns cart lines most-recently-added
        // first, so the LAST item here renders at the TOP of the cart. The surcharge
        // therefore goes first in this array and the garment second, which puts the
        // garment above its own surcharge in the drawer — the line the shopper actually
        // recognises, with the add-on beneath it.
        items: [
          // ALWAYS QUANTITY 1 — one delay choice per garment line, never per unit. The
          // lead time is a property of the LINE (the atelier fabricates that line on one
          // schedule), not of each garment in it. Mirroring the garment quantity here
          // also removed the only ceiling on this line: the garment is inventory-tracked
          // and Shopify caps it at available stock, while these add-ons must be UNTRACKED
          // (a sold-out tier would otherwise block the dress from being added at all).
          // Asking for N therefore always granted N surcharges even when the garment was
          // capped far below N. See #reconcileSurcharge for the residual case.
          {
            id: surcharge.variantId,
            quantity: 1,
            // Carries the garment link. NOT `parent_id`: that is only honoured for bundle
            // line items created by a Shopify Function, and /cart/add.js silently drops it
            // from a theme request (verified — the created line comes back with
            // `parent_id: null`). This property is what actually ties the surcharge to the
            // garment on the order record, so the admin/ERP does not see a standalone
            // "Délai raccourci" sale. The underscore prefix keeps it hidden from the
            // storefront while still landing on the order, matching the convention the
            // ShopPad payload used (_io_parent_order_group / _io_field_name).
            properties: { _delai_parent_variant_id: variantId },
          },
          { id: variantId, quantity, properties },
        ],
        // The loop above appends `sections` once per cart-items-component, so the values
        // repeat. Harmless as form fields, but worth collapsing before sending as JSON.
        sections: [
          ...new Set(
            formData
              .getAll("sections")
              .join(",")
              .split(",")
              .filter(Boolean)
          ),
        ].join(","),
      };

      // fetchConfig('javascript') DELETES Content-Type (correct for FormData, fatal for
      // JSON — Shopify would not parse the body and would 400). fetchConfig('json') keeps
      // it, so use that variant for the JSON payload.
      fetchCfg = fetchConfig("json", { body: JSON.stringify(payload) });
    }

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then(async (raw) => {
        // NORMALISE THE TWO RESPONSE SHAPES.
        // A FormData add returns the created line item at the TOP level. A JSON `items[]`
        // add returns `{ items: [...], sections }` instead, with no top-level line item —
        // so everything below, written for the FormData shape, would read undefined.
        // Flattening the first item (the garment) back up makes the handler shape-agnostic.
        //
        // Errors are NOT flattened: a rejection returns the usual
        // `{ status, message, description }`, which the error branch already understands.
        //
        // CAUTION — a rejection does NOT mean nothing was added. `items[]` is not atomic:
        // asking for more of a tracked garment than is in stock returns 422 while STILL
        // adding what was available, and the untracked surcharge line lands in full. The
        // error branch below therefore reconciles the cart instead of assuming it is
        // unchanged.
        // Find the GARMENT by variant id rather than by position. The array is ordered for
        // cart DISPLAY (surcharge first, garment last — see the payload above), so
        // `items[0]` is the surcharge, and everything downstream expects the garment.
        let response = raw;

        if (raw && Array.isArray(raw.items) && !raw.status) {
          const garment =
            raw.items.find(
              (item) => String(item.variant_id ?? item.id) === String(variantIdForReconcile)
            ) ?? raw.items[raw.items.length - 1];

          response = { ...raw, ...garment };
        }

        // Reframe ONLY when the rejected item is the add-on itself. Shopify's own message
        // is usually the more useful one — "3 articles seulement ont été ajoutés" tells
        // the shopper exactly what happened, and blanket-replacing it with "this lead time
        // is unavailable" throws away the only accurate explanation. The add-on case is
        // different: the shopper never knowingly chose that product, so a sold-out error
        // naming it is confusing. The raw message stays in `description` for support.
        if (surcharge && response.status && this.#isSurchargeRejection(response, surcharge)) {
          response = {
            ...response,
            message:
              Theme.translations?.delai_fabrication_error ||
              response.message,
            description: response.message,
          };
        }

        // A rejection may still have changed the cart (see the CAUTION above). Drop any
        // surcharge line that no longer has a garment line to belong to, so the shopper is
        // never left paying for a rush build on a dress that did not go in.
        //
        // AWAITED deliberately: the CartErrorEvent dispatched below triggers a cart-section
        // refresh, so firing this without waiting would race the removal and paint the
        // orphaned surcharge line into the drawer the shopper is about to look at. The
        // method swallows its own failures, so awaiting cannot block the error path.
        if (surcharge && response.status) {
          await this.#reconcileSurcharge(surcharge, variantIdForReconcile);
        }

        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors
            )
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton =
              this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector(
              ".add-to-cart-text--added"
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * Whether a rejection names the surcharge add-on rather than the garment.
   *
   * Shopify rejects by item, and its message quotes the offending product's title. When
   * the garment is the problem ("only 3 available") that message is the most useful thing
   * the shopper can read, so it must survive. Only when the add-on itself was refused is
   * the message confusing enough to replace — the shopper never knowingly picked that
   * product. Matching on the returned variant id first, and falling back to the title,
   * because Shopify does not always echo the id on a rejection.
   *
   * @param {*} response - The parsed error response.
   * @param {{ variantId: string, title?: string }} surcharge - The surcharge we sent.
   * @returns {boolean} True when the add-on is the rejected item.
   */
  #isSurchargeRejection(response, surcharge) {
    if (!response) return false;

    const rejectedId = response.id ?? response.variant_id;
    if (rejectedId != null && String(rejectedId) === String(surcharge.variantId)) {
      return true;
    }

    // These add-ons are untracked, so an availability rejection is almost never about
    // them. Absent a matching id, assume the garment was the problem and keep Shopify's
    // message.
    return false;
  }

  /**
   * Removes an orphaned surcharge line after a partial add.
   *
   * `/cart/add.js` with `items[]` is NOT atomic. Asking for more of an inventory-tracked
   * garment than is in stock returns 422 while still adding what was available — and the
   * surcharge, which must be untracked so it can never block the dress, always lands in
   * full. A rejection can therefore leave the cart holding a rush-fabrication charge for a
   * garment that is not there, or is there in smaller number than the shopper intended.
   *
   * Reads the cart back rather than trusting the response, because the response says
   * nothing about what survived. Removes the surcharge only when its garment line is
   * absent: a garment that landed short still has a legitimate single surcharge, which is
   * exactly the one-delay-per-line rule.
   *
   * Best-effort by design — a failure here must not mask the error already being shown to
   * the shopper, so it resolves quietly and lets the CartErrorEvent stand.
   *
   * @param {{ variantId: string }} surcharge - The surcharge that may be orphaned.
   * @param {string | null} garmentVariantId - The garment line it belongs to.
   * @returns {Promise<void>}
   */
  async #reconcileSurcharge(surcharge, garmentVariantId) {
    if (!garmentVariantId) return;

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
      }).then((res) => res.json());

      if (!cart || !Array.isArray(cart.items)) return;

      const garmentPresent = cart.items.some(
        (item) => String(item.variant_id) === String(garmentVariantId)
      );

      if (garmentPresent) return;

      // Match on the parent property, not the variant id alone: another garment in the
      // cart may legitimately carry the same tier, and that line must not be touched.
      const orphan = cart.items.find(
        (item) =>
          String(item.variant_id) === String(surcharge.variantId) &&
          item.properties?._delai_parent_variant_id === String(garmentVariantId)
      );

      if (!orphan) return;

      // Same call shape as component-cart-items.js, so the two paths stay consistent.
      await fetch(
        `${Theme.routes.cart_change_url}`,
        fetchConfig("json", {
          body: JSON.stringify({ id: orphan.key, quantity: 0 }),
        })
      );
    } catch (error) {
      // Deliberately swallowed. The shopper is already seeing the add-to-cart error; a
      // secondary failure here would replace an actionable message with a confusing one.
      console.warn("[delai] could not reconcile surcharge line", error);
    }
  }

  /**
   * Reads the selected production lead-time surcharge, if any.
   *
   * Returns null — meaning "behave exactly as before" — unless a lead-time picker is
   * present in THIS form, is visible, and has a checked option whose price is above zero.
   * A hidden picker never contributes: its radios are also disabled by the picker
   * component, but we check `hidden` here too so a phantom surcharge cannot ride along
   * with an in-stock purchase even if that disabling ever fails.
   *
   * @returns {{ variantId: string } | null} The surcharge variant, or null when none applies.
   */
  #getLeadTimeSurcharge() {
    const picker = this.querySelector("delai-fabrication-picker");

    if (!picker || picker.hasAttribute("hidden")) return null;

    // LAST-MOMENT GUARANTEE. The radios are named `properties[Délai de Fabrication]`, so
    // any other control using that name shares the radio group and can take the checked
    // flag away at any time — including after the picker's own connectedCallback has run
    // (the legacy ShopPad / Infinite Options widget does this on load). Re-asserting the
    // server's default here, at submit time, is the only point that cannot be raced: a
    // made-to-order garment must never reach the atelier with no lead-time instruction.
    // Only ever the 0 EUR tier, identified by price exactly as the server identifies it.
    if (!picker.querySelector("input[type='radio']:checked")) {
      const free = picker.querySelector("input[data-surcharge-price='0']");
      if (free instanceof HTMLInputElement && !free.disabled) free.checked = true;
    }

    const checked = picker.querySelector("input[type='radio']:checked");

    if (!(checked instanceof HTMLInputElement) || checked.disabled) return null;

    const variantId = checked.dataset.surchargeVariantId;
    const price = Number(checked.dataset.surchargePrice);

    // The free default option is a plain line-item property — no second cart line.
    if (!variantId || !Number.isFinite(price) || price <= 0) return null;

    return { variantId };
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Guides the shopper to pick a size when they tap add-to-cart with nothing
   * selected: scroll the variant picker into view and play a single attention
   * pulse on it. Never adds to cart. Respects prefers-reduced-motion (scroll
   * only, no pulse).
   */
  #guideToSizeSelection() {
    const section = this.closest(".shopify-section, dialog");
    const picker =
      section?.querySelector("variant-picker") ??
      document.querySelector("variant-picker");
    if (!picker) return;

    const target = picker.querySelector(".variant-picker__form") ?? picker;

    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    this.#setLiveRegionText(Theme?.translations?.choose_size ?? "");

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reducedMotion) return;

    // One-shot pulse: remove any in-flight pulse, force reflow, re-add so a
    // rapid second tap replays cleanly.
    target.classList.remove("variant-picker--pulse");
    void target.offsetWidth;
    target.classList.add("variant-picker--pulse");
    onAnimationEnd(target, () =>
      target.classList.remove("variant-picker--pulse")
    );
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector(
      '[ref="addToCartButton"]'
    );

    if (!currentAddToCartButton) return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true"
      );
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);

      // morph() runs childrenOnly, so it reconciles the button's label but not
      // the root button's own attributes. Sync data-choose-size explicitly from
      // the freshly rendered server fragment so the "choose a size" guard clears
      // (or re-applies) once a variant is selected/deselected.
      if (newAddToCartButton.dataset.chooseSize === "true") {
        currentAddToCartButton.dataset.chooseSize = "true";
      } else {
        delete currentAddToCartButton.dataset.chooseSize;
      }
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? "";

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100"
        );
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = "1";

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint
      );

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0";
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
